import { createContext, useMemo, useReducer } from 'react';

// ----------------------------------------------------------------------

type GlobalSettingsProps = {
  is_enabled: boolean;
  is_public: boolean;
  title: string;
  icon: string;
  logo: string;
  service_name: string;
  footer_text: string;
  category_text: string;
  search_placeholder: string;
  disallow_email: boolean,
  disallow_number: boolean,
  disallow_longtext: boolean,
  disallow_prefecture: boolean,
  tag_title: string;
  tag_show_limit: number,
  no_qa_result_message: string;
  no_result_title: string;
  no_result_message: string;
  retry_label: string;
  reset_label: string;
  tag_select_title: string;
  qa_list_title: string;
  show_qid: boolean;
  qa_answer_title: string;
  article_list_title: string;
  article_list_link_text: string;
  show_aid: boolean;
  view_history_title: string;
  feedback_title: string;
  yes_label: string;
  no_label: string;
  yes_message: string;
  no_message: string;
  no_reason_accuracy_label: string;
  no_reason_contents_label: string;
  no_reason_answer: string;
  comment_placeholder: string;
  comment_submit_label: string;
  comment_message: string;
  search_type: 'incremental' | 'submit' | '';
};

interface Props {
  children: JSX.Element | JSX.Element[];
}

// ----------------------------------------------------------------------

const GlobalSettingsContext = createContext<any>([]);

function globalSettingsReducer(
  state: GlobalSettingsProps,
  action: { type: string; payload: GlobalSettingsProps }
) {
  switch (action.type) {
    case 'loading': {
      return { ...state };
    }
    case 'success': {
      return { ...state, ...action.payload };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

const GlobalSettingsProvider = ({ children }: Props) => {
  const initialState: GlobalSettingsProps =  {
    is_enabled: true,
    is_public: false,
    title: '',
    service_name: '',
    footer_text: '',
    category_text: '',
    search_placeholder: '',
    disallow_email: false,
    disallow_number: false,
    disallow_longtext: true,
    disallow_prefecture: false,
    tag_title: '',
    tag_show_limit: 20,
    no_qa_result_message: '',
    no_result_title: '',
    no_result_message: '',
    retry_label: '',
    reset_label: '',
    tag_select_title: '',
    qa_list_title: '',
    show_qid: true,
    qa_answer_title: '',
    article_list_title: '',
    article_list_link_text: '',
    show_aid: true,
    view_history_title: '',
    feedback_title: '',
    yes_label: '',
    no_label: '',
    yes_message: '',
    no_message: '',
    no_reason_accuracy_label: '',
    no_reason_contents_label: '',
    no_reason_answer: '',
    comment_placeholder: '',
    comment_submit_label: '',
    comment_message: '',
    icon: '',
    logo: '',
    search_type: '',
  };

  const [state, dispatch] = useReducer(globalSettingsReducer, initialState);
  const value = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch]
  );

  return <GlobalSettingsContext.Provider value={value}>{children}</GlobalSettingsContext.Provider>;
};

export { GlobalSettingsProvider, GlobalSettingsContext };
